/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { apiClient } from '@lib/api-client';
import { useQuery } from '@tanstack/react-query';

// Types
import { NOT_APPLICABLE } from '@config/constants/common';
import { QueryConfig } from '@lib/react-query';
import { numericTokenEquivalent } from '@utils/formats';
import {
	convertProduction,
	formatDate,
	parseTokenEquivalentToTCO2E,
} from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import isNil from 'lodash/isNil';
import {
	TAco2Token,
	TApiResponseAco2Token,
	TProductionDetail,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getAco2TokenById = (
	id: string
): Promise<TApiResponseAco2Token> => {
	return apiClient.get(`/aco2_tokens/${id}`, {
		params: {
			populate: [
				'project',
				'details.site',
				'productions.site',
				'productions.organization',
				'productions.project',
				'productions.entity',
			],
			//excludeFields: ['details'],
		},
	});
};

/* ---------------------------------------------------------------------------- */
/*                                     Type                                    */
/* -------------------------------------------------------------------------- */

type TUseAco2TokenByIdOptions = {
	id: string;
	queryConfig?: QueryConfig<typeof getAco2TokenById>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useAco2TokenById = ({
	id,
	queryConfig,
}: TUseAco2TokenByIdOptions) => {
	const { data, ...restQuery } = useQuery({
		queryKey: ['aco2-tokens-by-id', id],
		queryFn: () => getAco2TokenById(id),
		enabled: !!id,
		...queryConfig,
	});

	const updateList = (mintHistory: TAco2Token) => {
		const _details = mintHistory?.details || [];
		const _productions = mintHistory?.productions || [];

		const res = _details.reduce(
			(acc: any, currentItem: TProductionDetail) => {
				const _foundProduction =
					Array.isArray(_productions) &&
					_productions.find(
						(item) => item.id == currentItem.production
					);

				const conversionFactorRatio =
					currentItem.conversionFactorRatio
						? currentItem.conversionFactorRatio
						: 0;

				const fcApplicable = (
					conversionFactorRatio / 1000
				).toFixed(3);

				const convertionFactor = !isNil(
					currentItem?.conversionFactorRatio
				)
					? currentItem?.conversionFactorRatio
					: 0;

				const unitLabels: Record<string, string> = {
					kwh: getUnitLabel('gCO2perKwh'),
					kg: getUnitLabel('gCO2perKg'),
				};

				const unit =
					unitLabels[
						_foundProduction?.unit.toLowerCase()
					] || NOT_APPLICABLE;

				const modifiedItem = {
					verified: _foundProduction?.verified || false,
					tokenEquivalent: parseTokenEquivalentToTCO2E(
						convertionFactor,
						currentItem.quantity
					),
					fcApplicable: `${fcApplicable} ${unit}`,
					startDate: formatDate(
						currentItem.startDate,
						'DD/MM/YYYY'
					),
					endDate: formatDate(
						currentItem.endDate,
						'DD/MM/YYYY'
					),

					production: convertProduction(
						_foundProduction.quantity,
						_foundProduction.unit
					),

					numericTokenEquivalent: numericTokenEquivalent(
						convertionFactor,
						currentItem.quantity
					),

					organization: {
						name:
							_foundProduction?.organization?.name ||
							NOT_APPLICABLE,
					},
					site:
						_foundProduction?.site?.name ||
						NOT_APPLICABLE,
					entity: {
						name:
							_foundProduction?.entity?.name ||
							NOT_APPLICABLE,
					},
					project:
						_foundProduction?.project?.name ||
						NOT_APPLICABLE,
					aco2Token: { mint: { status: '' } },
					mintStatus:
						_foundProduction?.status || NOT_APPLICABLE,
				};

				acc.push(modifiedItem);

				return acc;
			},
			[]
		);

		return res;
	};

	return {
		data: data ? updateList(data) : [],
		blockchain: data?.blockchain
			? data.blockchain
			: NOT_APPLICABLE,
		transactionHash: data?.transactionHash
			? data.transactionHash
			: NOT_APPLICABLE,
		...restQuery,
	};
};
